/**
 * Page header: title, one-line explanation, and the two period controls.
 *
 * THE COMPARISON CONTROL HAS AN "AUTOMATISCH" OPTION, AND THAT IS THE POINT.
 * The backend rejects exactly one of `comparison_year` / `comparison_month`,
 * because completing the missing half from the selected month would compare
 * against a period nobody chose. Rather than validating a half state after the
 * fact, the control makes it unreachable: either "Automatisch (Vormonat)" —
 * which sends neither param and lets B2 resolve the preceding month — or a
 * concrete month, which sends both.
 *
 * Each period is ONE select holding "August 2026" rather than a month select
 * beside a year select. Two controls can express "Februar 2020" by accident on
 * the way from "August 2026" to "August 2020"; one cannot, and it fires one
 * request instead of two.
 *
 * No export button. There is no export endpoint, and a button that produces a
 * client-side reconstruction of a financial contract would be a second, weaker
 * source of the same numbers.
 */
import React, { useMemo } from "react";
import { CalendarDays } from "lucide-react";

import { MONTHS_DE } from "./revenueAnalyticsFormat";
import { currentBusinessPeriod } from "../../../utils/datetime";

/** Sentinel for "let the backend resolve the preceding month". */
export const COMPARISON_AUTO = "auto";

/**
 * The selectable months.
 *
 * Two years back through next year — the same window the existing Bexio period
 * control offers, so the two finance surfaces do not disagree about how far a
 * user may look.
 */
function useMonthOptions() {
  return useMemo(() => {
    const thisYear = currentBusinessPeriod().year;
    const out = [];
    for (let y = thisYear - 2; y <= thisYear + 1; y += 1) {
      for (let m = 1; m <= 12; m += 1) {
        out.push({ value: `${y}-${m}`, year: y, month: m, label: `${MONTHS_DE[m - 1]} ${y}` });
      }
    }
    return out;
  }, []);
}

function parseValue(value) {
  const [y, m] = String(value).split("-");
  return { year: Number(y), month: Number(m) };
}

/**
 * One labelled control box, styled after the reference: a small caption above
 * the value, a calendar glyph on the right, the whole thing one bordered field.
 */
function PeriodField({ id, label, value, onChange, options, extraOption }) {
  return (
    <div className="relative flex min-w-[190px] flex-col rounded-lg border border-border bg-card px-3 py-1.5 focus-within:ring-2 focus-within:ring-ring">
      <label htmlFor={id} className="text-[11px] leading-tight text-muted-foreground">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full cursor-pointer appearance-none bg-transparent py-0.5 text-sm font-medium text-foreground focus:outline-none"
        >
          {extraOption ? (
            <option value={extraOption.value}>{extraOption.label}</option>
          ) : null}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <CalendarDays aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    </div>
  );
}

export default function RevenueAnalyticsHeader({
  year,
  month,
  comparisonYear,
  comparisonMonth,
  onPeriodChange,
  onComparisonChange,
  resolvedComparisonLabel,
}) {
  const options = useMonthOptions();

  const comparisonValue =
    comparisonYear != null && comparisonMonth != null
      ? `${comparisonYear}-${comparisonMonth}`
      : COMPARISON_AUTO;

  return (
    <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Umsatz &amp; Miete – Analyse
        </h1>
        <p className="mt-1 max-w-[640px] text-sm text-muted-foreground">
          Erwartete Miete, Fakturierung, Zahlungsstand und Forderungen im Überblick.
        </p>
      </div>

      <div className="flex flex-wrap items-start gap-3">
        <PeriodField
          id="revenue-analytics-period"
          label="Monat"
          value={`${year}-${month}`}
          options={options}
          onChange={(v) => {
            const p = parseValue(v);
            onPeriodChange(p.year, p.month);
          }}
        />
        <div className="flex flex-col">
          <PeriodField
            id="revenue-analytics-comparison"
            label="Vergleichsmonat"
            value={comparisonValue}
            options={options}
            extraOption={{ value: COMPARISON_AUTO, label: "Automatisch (Vormonat)" }}
            onChange={(v) => {
              if (v === COMPARISON_AUTO) {
                onComparisonChange(null, null);
                return;
              }
              const p = parseValue(v);
              onComparisonChange(p.year, p.month);
            }}
          />
          {/* When "Automatisch" is selected the user still deserves to know
              WHICH month they are being compared against — and that answer
              comes from the response, never from a local subtraction. */}
          {comparisonValue === COMPARISON_AUTO && resolvedComparisonLabel ? (
            <span className="mt-1 pl-3 text-[11px] text-muted-foreground">
              Vergleich: {resolvedComparisonLabel}
            </span>
          ) : null}
        </div>
      </div>
    </header>
  );
}
